// components/StepsSection/StepsSection.js
import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, Container, Grid, useTheme } from "@mui/material";
import Lottie from "lottie-react";

// Import your Lottie animations
import createProfileAnimation from "../../assets/lottie/create-profile.json";
import searchAnimation from "../../assets/lottie/search-for-roommates.json";
import updateProfileAnimation from "../../assets/lottie/Update-your-profile.json";
import createPostAnimation from "../../assets/lottie/create-post.json";

const StepsSection = ({ isMobile }) => {
  const theme = useTheme();

  const steps = [
    {
      number: "01",
      title: "Create Your Profile",
      description: "Start by creating an account with your email and a secure password. Showcase your personality and lifestyle preferences.",
      animation: createProfileAnimation,
      color: "#00ff88",
      icon: "👤"
    },
    {
      number: "02",
      title: "Discover Roommates",
      description: "Browse through potential roommates who match your vibe. Use smart filters to find your perfect living companion.",
      animation: searchAnimation,
      color: "#0099ff",
      icon: "🔍"
    },
    {
      number: "03",
      title: "Customize Your Profile",
      description: "Upload photos, set your preferences, and highlight what makes you an awesome roommate. Be authentic!",
      animation: updateProfileAnimation,
      color: "#ff3366",
      icon: "✨"
    },
    {
      number: "04",
      title: "Connect & Chat",
      description: "Found someone interesting? Start a conversation and plan your first meetup. Your future roommate awaits!",
      animation: createPostAnimation,
      color: "#ffcc00",
      icon: "💬"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const stepVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9 
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      sx={{
        py: 10,
        background: "linear-gradient(180deg, rgba(0, 29, 61, 0.9) 0%, rgba(0, 8, 20, 1) 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 255, 136, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 153, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255, 51, 102, 0.05) 0%, transparent 50%)
          `,
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant={isMobile ? "h3" : "h2"}
            align="center"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(45deg, #00ff88, #0099ff)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            How It Works
          </Typography>
          
          <Typography
            variant="h6"
            align="center"
            sx={{
              color: "rgba(255,255,255,0.8)",
              mb: 8,
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            Find your perfect roommate in just 4 simple steps
          </Typography>
        </motion.div>

        {/* Steps Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  variants={stepVariants}
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                >
                  <Box
                    sx={{
                      background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid rgba(255,255,255,0.1)`,
                      borderRadius: "20px",
                      p: { xs: 3, sm: 4 },
                      pt: { xs: 5, sm: 6 },
                      pr: { xs: 4, sm: 6 },
                      height: "100%",
                      position: "relative",
                      overflow: "hidden",
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${step.color}, transparent)`,
                      },
                      '&:hover': {
                        border: `1px solid ${step.color}40`,
                      }
                    }}
                  >
                    {/* Step Number - inside card with padding so it doesn't get cropped */}
                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "4rem", sm: "5rem", md: "6rem" },
                        fontWeight: 900,
                        color: `${step.color}15`,
                        position: "absolute",
                        top: 16,
                        right: 24,
                        lineHeight: 1,
                      }}
                    >
                      {step.number}
                    </Typography>

                    {/* Content */}
                    <Box sx={{ position: "relative", zIndex: 2 }}>
                      {/* Icon and Animation - same height as UserStoryCard Lottie (200px) */}
                      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <motion.div
                          animate={{ 
                            scale: [1, 1.2, 1],
                            rotate: [0, 5, -5, 0]
                          }}
                          transition={{ 
                            duration: 3,
                            repeat: Infinity,
                            delay: index * 0.5 
                          }}
                          style={{ marginRight: "1rem" }}
                        >
                          <Typography sx={{ fontSize: "2.5rem" }}>
                            {step.icon}
                          </Typography>
                        </motion.div>
                        
                        <Box sx={{ flex: 1, height: 200 }}>
                          <Lottie
                            animationData={step.animation}
                            style={{ width: "100%", height: "100%" }}
                            loop
                            autoplay
                          />
                        </Box>
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          color: "white",
                        }}
                      >
                        {step.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          color: "rgba(255,255,255,0.8)",
                          lineHeight: 1.6,
                        }}
                      >
                        {step.description}
                      </Typography>

                      {/* Progress Line (for last step only) */}
                      {index !== steps.length - 1 && !isMobile && (
                        <motion.div
                          initial={{ scaleX: 0 }}
                          whileInView={{ scaleX: 1 }}
                          transition={{ delay: 0.5 + index * 0.2, duration: 0.8 }}
                          viewport={{ once: true }}
                          style={{
                            position: "absolute",
                            right: -32,
                            top: "50%",
                            width: "64px",
                            height: "2px",
                            background: `linear-gradient(90deg, ${step.color}, ${steps[index + 1].color})`,
                            transformOrigin: "left center",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          viewport={{ once: true }}
          style={{ textAlign: "center", marginTop: "4rem" }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.8)",
              mb: 3,
            }}
          >
            Ready to start your journey?
          </Typography>
          
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity 
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(45deg, #00ff88, #0099ff)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Let's Find Your Match! 🎯
            </Typography>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default StepsSection;